import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const appearance = {
    theme: 'stripe',
    variables: {
        colorPrimary: '#3273dc',
        borderRadius: '4px',
        fontFamily: 'BlinkMacSystemFont, -apple-system, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "Helvetica", "Arial", sans-serif'
    }
};

function CheckoutForm({ amountPence, redirectFlowId, invoiceDate, onSuccess, onCancel }) {
    const stripe = useStripe();
    const elements = useElements();
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        setErrorMessage(null);

        // return_url is the 3DS fallback — Stripe appends payment_intent to it
        const returnUrl = `${window.location.origin}/confirmation?redirect_flow_id=${redirectFlowId}&invoice_date=${invoiceDate}`;

        const { paymentIntent, error } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
        });

        if (error) {
            setErrorMessage(error.message);
            setLoading(false);
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            onSuccess(paymentIntent.id);
        } else {
            setErrorMessage('Payment could not be confirmed. Please try again.');
            setLoading(false);
        }
    };

    const amountGbp = (amountPence / 100).toFixed(2);

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            {errorMessage && (
                <p className="has-text-danger mt-3">{errorMessage}</p>
            )}
            <div className="field is-grouped mt-4">
                <div className="control">
                    <button
                        className="button is-primary"
                        type="submit"
                        disabled={!stripe || loading}
                    >
                        {loading
                            ? <span className="icon"><i className="fas fa-spinner fa-pulse" /></span>
                            : `Pay £${amountGbp}`}
                    </button>
                </div>
                <div className="control">
                    <button
                        className="button is-light"
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Back
                    </button>
                </div>
            </div>
        </form>
    );
}

export function StripePaymentForm({ clientSecret, publishableKey, amountPence, redirectFlowId, invoiceDate, onSuccess, onCancel }) {
    const stripePromise = loadStripe(publishableKey);

    const amountGbp = (amountPence / 100).toFixed(2);

    return (
        <div className="card selected mt-4">
            <div className="card-header">
                <div className="card-header-title is-centered">
                    <h2 className="subtitle is-5">Card Payment &mdash; £{amountGbp}</h2>
                </div>
            </div>
            <div className="card-content">
                <p className="mb-4">
                    Please enter your card details to pay your one-off connection and router charges. Your monthly subscription will continue to be collected via Direct Debit.
                </p>
                <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
                    <CheckoutForm
                        amountPence={amountPence}
                        redirectFlowId={redirectFlowId}
                        invoiceDate={invoiceDate}
                        onSuccess={onSuccess}
                        onCancel={onCancel}
                    />
                </Elements>
                <p className="has-text-grey is-size-7 mt-4">
                    <span className="icon-text">
                        <span className="icon"><i className="fas fa-lock"></i></span>
                        <span>Payments are processed securely by <a href="https://stripe.com" target="_blank" rel="noreferrer">Stripe</a>. 1310 does not store your card details.</span>
                    </span>
                </p>
            </div>
        </div>
    );
}
